<template>
  <div class="container-fluid-full">
    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-6">
          <div class="page-title m-b-none">
            <h1>
              Run Details
              <span class="status status-success">{{ deliveryRun.status }}</span>
            </h1>
          </div>
        </div>
        <div class="col-xs-6 text-right">
          <div class="run-sheet-actions m-t-lg">
            <router-link
              type="button"
              class="btn btn-default"
              :to="{ name: 'delivery-runs.run-sheet', params: { id } }"
              exact
            >
              <font-awesome :icon="['far', 'route']"/>
              Run Sheet
            </router-link>
          </div>
        </div>
      </div>
      <!-- /.row -->
    </div>

    <div class="run-main">
      <validation-observer ref="validation" v-slot="{ validate }" tag="div" class="run-left">
        <div class="run-left-top">
          <div class="border-bottom p-sm">
            <div class="row">
              <div class="col-xs-2">
                <button type="button" class="btn btn-white" @click="previousStop">
                  <font-awesome :icon="['far', 'chevron-left']"/>
                </button>
              </div>
              <div class="col-xs-8">
                <p class="text-center text-truncate m-b-none">
                  <strong class="small">Stop {{ stop.order }} of {{ deliveryRun.stops_count }}</strong>
                  <br />{{ stop.station.name }}
                  <span class="badge badge-green" v-if="stop.complete">Complete</span>
                </p>
              </div>
              <div class="col-xs-2 text-right">
                <button type="button" class="btn btn-white" @click="nextStop">
                  <font-awesome :icon="['far', 'chevron-right']"/>
                </button>
              </div>
            </div>
          </div>

          <div class="p-sm">
            <div class="row">
              <div class="col-md-8">
                <div class="form-group">
                  <p class="strong"><font-awesome :icon="['fas', 'link']" v-if="stop.linked_station" /> {{ stop.linked_station }}</p>
                  <p>
                    <strong>Address</strong>
                    <br />{{ stop.station.address }}
                  </p>
                </div>
                <!-- /.for-group -->
              </div>
              <div class="col-md-4 text-right">
                <a :href="navigateToGoogleMapsUrl" target="_blank" type="button" class="btn btn-white">
                  <font-awesome :icon="['far', 'map-marked-alt']"/>
                  Navigate To {{ isLastStepCompleted ? 'Depot' : '' }}
                </a>
              </div>
            </div>
            <!-- /.row -->

            <div class="row">
              <div class="col-xs-6">
                <p><strong>Station Number:</strong> {{ stop.station.code }}</p>
              </div>
              <div class="col-xs-6"><p><strong>Zone:</strong> {{ stop.station.zone_number }}</p>
              </div>
            </div>

            <p>
              <strong>Number of bags requested:</strong> {{ stop.pickup }}
            </p>

            <p>
              <strong>Amount picked up:</strong>
            </p>

            <validation-provider name="bags picked up" rules="required" v-slot="{ classes, errors }">
              <div class="form-group" :class="classes">
                <div class="input-group">
                  <input type="text" class="form-control" v-model="bags"/>
                  <span class="input-group-addon">bags</span>
                </div>
                <help-block v-bind="{ errors }" class="help-block"/>
              </div>
            </validation-provider>
          </div>
        </div>

        <div class="run-left-main">
          <ul class="list-data has-multi-checkbox no-links no-hover m-t-none">
            <li class="data-heading sticky">
              <div class="form-check checkbox checkbox-success">
                <input :id="`checkbox${deliveryRun.id}`" class="styled" type="checkbox" :checked="selectedAll" @click="handleToggleAllGarments" />
                <label :for="`checkbox${deliveryRun.id}`"></label>
              </div>
              <div class="row">
                <div class="col-xs-5">
                  Firefighter
                </div>
                <div class="col-xs-4">Garment Type</div>
                <div class="col-xs-3">Serial</div>
              </div>
              <!-- /.row -->
            </li>
            <li v-for="garment in stop.garments" :key="garment.id" @click.prevent="handleSelectGarment(garment)">
              <div class="form-check checkbox checkbox-success">
                <input :id="`checkbox${garment.id}`" class="styled" type="checkbox" :checked="garment.delivered" />
                <label :for="`checkbox${garment.id}`"></label>
              </div>
              <div class="row">
                <div class="col-xs-5">
                  <p class="strong">{{garment.firefighter}}</p>
                </div>
                <div class="col-xs-4">
                  {{garment.name}}
                </div>
                <div class="col-xs-3">
                  <p class="text-muted">{{garment.serial}}</p>
                </div>
              </div>
              <!-- /.row -->
            </li>
          </ul>
          <!-- /.list-data -->
        </div>

        <div class="run-left-footer text-right" v-if="deliveryRun.editable">
          <async-button type="button" class="btn btn-success" :validate="validate" :click="complete">
            <template v-if="stop.complete">
              Save Changes
            </template>
            <template v-else>
              Complete Stop
            </template>
          </async-button>
        </div>
      </validation-observer>

      <div class="run-right">
        <run-sheet-driver-map v-if="stopExists" v-bind="{ stop }"/>
      </div>
    </div>
  </div>
</template>

<script>
import { get, call, sync } from 'vuex-pathify'
import { toNumber, clone } from 'lodash'
import { handleServerErrors } from 'utils'
import RunSheetDriverMap from './RunSheetDriverMap'
import swal from 'sweetalert'

export default {
  props: ['id', 'stopId'],

  components: {
    RunSheetDriverMap
  },

  data: () => ({
    selectedAll: true
  }),

  watch: {
    selectedAll: {
      handler (delivered) {
        this.toggleAll(delivered)
      },
      immediate: true
    },
    stopId () {
      if (!this.stop.complete) {
        this.toggleAll(this.selectedAll)
      }
    }
  },

  computed: {
    ...get('deliveryRuns', ['deliveryRunExists', 'deliveryRun']),

    deliveryRunStatus: sync('deliveryRuns/deliveryRun@status'),

    bags: {
      get () {
        return this.stop.bags
      },

      set (bags) {
        const { stop } = this

        this.setDeliveryRunStop(Object.assign({}, stop, { bags }))
      }
    },

    isLastStepCompleted () {
      return this.stop.order === this.deliveryRun.stops_count && this.stop.complete
    },

    navigateToGoogleMapsUrl () {
      // if the last step is completed, navigate drive back to the office
      if (this.isLastStepCompleted) {
        return this.toGoogleMapUrl(process.env.VUE_APP_DEPOT_LATITUDE, process.env.VUE_APP_DEPOT_LONGITUDE)
      }

      if (this.stopExists) {
        const { latitude, longitude } = this.stop.station

        return this.toGoogleMapUrl(latitude, longitude)
      }

      return null
    },

    stopExists () {
      return this.stopIndex !== -1
    },

    stopIndex () {
      if (this.deliveryRunExists && this.stopId) {
        return this.deliveryRun.stops.findIndex(({ id }) => id === toNumber(this.stopId))
      }

      return -1
    },

    stop () {
      if (this.deliveryRunExists && this.stopId) {
        return this.deliveryRun.stops.find(({ id }) => id === toNumber(this.stopId))
      }

      return {
        station: {},
        bags: null,
        order: null,
        garments: []
      }
    },

    isSelectedAllGarments () {
      return this.stop.garments.filter(garment => garment.delivered).length === this.stop.garments.length
    }
  },

  async created () {
    await this.getDeliveryRun(this.id)

    // Go to first incomplete stop in for the given delivery run, or, if there's no incomplete
    // stops just go to the first.
    if (!this.stopId) {
      this.nextIncompleteStop(true, this.deliveryRun.stops[0])
    }
  },

  beforeDestroy () {
    this.resetDeliveryRun()
  },

  methods: {
    ...call('deliveryRuns', ['getDeliveryRun', 'resetDeliveryRun', 'setDeliveryRunStop', 'updateDeliveryRunStop']),

    toGoogleMapUrl (latitude, longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`
    },

    nextIncompleteStop (replace = false, defaultStop = {}) {
      const { id: stopId } = this.deliveryRun.stops.find(({ complete }) => !complete) || defaultStop

      if (stopId) {
        this.$router[replace ? 'replace' : 'push']({ name: 'delivery-runs.run-sheet.stop', params: { stopId } })

        this.$refs.validation.reset()
      }
    },

    previousStop () {
      this.gotoStop(-1)
    },

    nextStop () {
      this.gotoStop(1)
    },

    gotoStop (direction) {
      if (this.stopIndex === -1) {
        return
      }

      // When paging through the stops we'll null out the bags picked up.
      this.pickedUp = null
      this.$refs.validation.reset()

      const totalStops = this.deliveryRun.stops.length

      let gotoStopIndex = this.stopIndex + direction

      if (gotoStopIndex < 0) {
        gotoStopIndex = totalStops - 1
      } else if (gotoStopIndex > totalStops - 1) {
        gotoStopIndex = 0
      }

      const { id: stopId } = this.deliveryRun.stops[gotoStopIndex]

      this.$router.push({ name: 'delivery-runs.run-sheet.stop', params: { stopId } })
    },

    async complete () {
      if (!this.isSelectedAllGarments) {
        if (
          !await swal({
            text: `
              Not all garments have been delivered, please press Cancel to update missed garments.
              If Continue is pressed, missed garments will return back to pending delivery. `,
            dangerMode: true,
            buttons: ['Cancel', 'Continue']
          })
        ) {
          return
        }
      }

      const { id, stop } = this

      try {
        const meta = await this.updateDeliveryRunStop({ id, stop })

        if (meta.delivery_run_completed) {
          await this.getDeliveryRun(id)
        }

        this.nextIncompleteStop()
      } catch (error) {
        if (!handleServerErrors(error, this.$refs.validation)) {
          this.$notify('error', 'Something went wrong when completing the stop.')
        }
      }
    },

    handleSelectGarment (gar) {
      if (gar.delivered && !gar.local) {
        return
      }

      const { stop } = this

      const garments = clone(this.stop.garments)

      let garment = garments.find((item) => item.id === gar.id)
      garment.delivered = !garment.delivered
      garment.local = true

      this.setDeliveryRunStop(Object.assign({}, stop, { garments }))
    },

    handleToggleAllGarments () {
      this.selectedAll = !this.selectedAll
    },

    toggleAll (delivered) {
      this.setDeliveryRunStop({
        ...this.stop,
        garments: this.stop.garments.map(garment => ({ ...garment, delivered, local: true }))
      })
    }
  }
}
</script>
